import { useT } from 'apprise-frontend-core/intl/language';
import { useTimeProvider } from 'apprise-ui/time/provider';
import dateformat from 'date-fns-tz/format';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import zonedTimeToUtc from 'date-fns-tz/zonedTimeToUtc';

export type Time = number | string | Date



/* 
    helps with storing and presenting time wrt to a given timezone, rather than the current timezone.
    the 'simulated' zone is usually the canonical timezone of the application, most often UTC (the default).

    this is required because all Dates hold UTC timestamps that are rendered and often created relative to the current timezone.
    in CET, for example, a local time of 10:30 is stored as 9:30 UTC and then displayed "back" as 10:30.

    so in the simulation Dates have to be 'transformed' to hold different timestamps:

    - on creation, the new timestamps are those that capture the local time in the simulated timezone. 
      eg. when simulating UTC, 10:30 CET becomes 10:30 UTC.

      specifically, timestamps shift of the difference between current and target offsets (CURRENT-TARGET).
      eg. in the example above, the shift is 1hr forward.
    
    
    - on render, the new timestamps are those that show local time like in the simulated timezone when interpreted relative to the current timezone. 
      eg. when simulating UTC, 10:30 UTC but become 9:30 UTC so that will display as 10:30 in CET. 
    
     specifically, timestamp shift of the difference between target and current offsets (TARGET-CURRENT).
     e.g. if the example above the shift is 1hr backwards. 

*/

export const useZonedTime = () => {


    const t = useT()

    const { defaultFormat } = useTimeProvider()


    const self = {

        /*
        
            interprets local time as it would be in a target timezone (eg. 10:30 CET as 10:30 PST).
        
        */

        localiseFor: (time: Time, timezone: string) => zonedTimeToUtc(time, timezone),


        /*

            adjusts time so that it renders locally as it would in a target timezone  (eg. 10:30 CET to 1:30 PST).

            use in place of format() if render is delegated to a timezone-naive component/context.

         */

        adjustFrom: (time: Time, timezone: string) => utcToZonedTime(time, timezone),



        /* 

            remders time as it would be displyed in a target timezone. 
            if ommitted, the format is the default format in its 'short' variant.
           
        */

        format: (time: Time, timezone: string, format = `${defaultFormat}_short`) => dateformat(self.adjustFrom(time, timezone), t(`time.format_${format}`, format))


    }

    return self
}



/*
    simplifies the API of useZonedTime() when working with a single timezone, often the canonical timezone of the application.

 */
export const useTimezone = (timezone?: string) => {

    const { tz: canonical } = useTimeProvider()

    const zone = timezone ?? canonical

    const zoned = useZonedTime()

    return {

        intern: (time: Time) => zoned.localiseFor(time, zone),
        extern: (time: Time) => zoned.adjustFrom(time, zone),
        format: (time: Time, format?: string) => zoned.format(time, zone, format)

    }


}